/**
 * SurfaceRouter - URL-based Surface Navigation
 * Phase 11: Navigation & Routing
 * 
 * Handles URL parsing, surface resolution, and browser history management.
 * Integrates with NavigationManager and SurfaceRegistry for seamless routing.
 */

import { useEffect, useCallback, useRef, useState } from 'react';

/**
 * Route Pattern Definition
 */
export interface RoutePattern {
  /** URL pattern (e.g., "/page/:id") */
  pattern: string;
  /** Surface name to render */
  surface: string;
  /** Regex for matching */
  regex: RegExp;
  /** Parameter names */
  params: string[];
}

/**
 * Resolved Route Information
 */
export interface ResolvedRoute {
  /** Surface name */
  surface: string;
  /** Route parameters */
  params: Record<string, string>;
  /** Query parameters */
  query: Record<string, string>;
  /** Is this route valid? */
  isValid: boolean;
  /** Error message if invalid */
  error?: string;
}

/**
 * Router Configuration
 */
export interface RouterConfig {
  /** Base URL path */
  basePath?: string;
  /** Enable URL syncing */
  enableUrlSync?: boolean;
  /** Enable browser history */
  enableHistory?: boolean;
  /** 404 fallback surface */
  notFoundSurface?: string;
  /** Debug logging */
  debug?: boolean;
}

/**
 * SurfaceRouter Class
 * 
 * Core routing logic for URL-based surface navigation.
 */
export class SurfaceRouter {
  private routes: Map<string, RoutePattern> = new Map();
  private config: Required<RouterConfig>;
  private initialized = false;
  private currentUrl: string = '';
  private validSurfaces: Set<string> = new Set();

  constructor(config: RouterConfig = {}) {
    this.config = {
      basePath: '',
      enableUrlSync: true,
      enableHistory: true,
      notFoundSurface: 'HomePage',
      debug: false,
      ...config
    };

    this.setupRoutes();
  }

  /**
   * Initialize the router with valid surfaces
   */
  public initialize(validSurfaces: string[]): void {
    this.validSurfaces = new Set(validSurfaces);
    this.initialized = true;
    this.log('Router initialized with surfaces:', Array.from(this.validSurfaces));
  }

  /**
   * Setup URL routes
   */
  private setupRoutes(): void {
    // Root route
    this.addRoute('/', 'HomePage', []);

    // Page editor route
    this.addRoute('/page/:pageId', 'PageEditor', ['pageId']);

    // Database route
    this.addRoute('/database/:databaseId', 'DatabaseView', ['databaseId']);

    // Peek surfaces (can be combined with main routes)
    this.addRoute('/peek/ai', 'AIChat', []);
    this.addRoute('/peek/notes', 'QuickNote', []);
  }

  /**
   * Add a route pattern
   */
  private addRoute(pattern: string, surface: string, params: string[]): void {
    // Convert pattern to regex
    // /page/:pageId -> /page/([^/]+)
    let regexPattern = pattern
      .replace(/\//g, '\\/')
      .replace(/:[^/]+/g, '([^/]+)');

    const regex = new RegExp(`^${regexPattern}$`);

    this.routes.set(pattern, {
      pattern,
      surface,
      regex,
      params
    });

    this.log(`Route added: ${pattern} -> ${surface}`);
  }

  /**
   * Parse URL to extract surface and parameters
   */
  private parseUrl(url: string): {
    path: string;
    query: Record<string, string>;
  } {
    // Remove base path
    let path = url.replace(this.config.basePath, '');

    // Split path and query
    const [pathPart, queryPart] = path.split('?');

    // Parse query parameters
    const query: Record<string, string> = {};
    if (queryPart) {
      new URLSearchParams(queryPart).forEach((value, key) => {
        query[key] = value;
      });
    }

    return {
      path: pathPart || '/',
      query
    };
  }

  /**
   * Resolve URL to surface and parameters
   */
  public resolveRoute(url: string): ResolvedRoute {
    const { path, query } = this.parseUrl(url);

    this.log(`Resolving route: ${path}`);

    // Try to match against routes
    for (const [pattern, route] of this.routes) {
      const match = path.match(route.regex);
      if (match) {
        const params: Record<string, string> = {};

        // Extract parameters
        route.params.forEach((paramName, index) => {
          params[paramName] = match[index + 1];
        });

        const isValid = this.validSurfaces.size === 0 || this.validSurfaces.has(route.surface);

        const resolved: ResolvedRoute = {
          surface: route.surface,
          params,
          query,
          isValid
        };

        if (!resolved.isValid) {
          resolved.error = `Surface not found: ${route.surface}`;
        }

        this.log(`Route resolved: ${route.surface}`, resolved);
        return resolved;
      }
    }

    // No route matched - 404
    this.log(`No route matched for: ${path}`);

    return {
      surface: this.config.notFoundSurface,
      params: {},
      query,
      isValid: true,
      error: `Route not found: ${path}`
    };
  }

  /**
   * Get current URL
   */
  public getCurrentUrl(): string {
    if (this.currentUrl) {
      return this.currentUrl;
    }

    if (typeof window !== 'undefined') {
      return window.location.pathname + window.location.search;
    }

    return '/';
  }

  /**
   * Set current URL
   */
  public setCurrentUrl(url: string): void {
    this.currentUrl = url;
  }

  /**
   * Get current route
   */
  public getCurrentRoute(): ResolvedRoute {
    return this.resolveRoute(this.getCurrentUrl());
  }

  /**
   * Build URL from surface and params
   */
  public buildUrl(surface: string, params: Record<string, any> = {}): string {
    // Find route for surface
    for (const route of this.routes.values()) {
      if (route.surface === surface) {
        let url = route.pattern;

        // Replace parameters
        route.params.forEach(paramName => {
          const value = params[paramName];
          if (value) {
            url = url.replace(`:${paramName}`, String(value));
          }
        });

        // Add query parameters
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (!route.params.includes(key) && value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        return url + (queryString ? `?${queryString}` : '');
      }
    }

    // Surface not found in routes
    return '/';
  }

  /**
   * Update browser URL
   */
  public updateBrowserUrl(url: string, replace: boolean = false): void {
    if (!this.config.enableUrlSync || typeof window === 'undefined') {
      return;
    }

    const fullUrl = this.config.basePath + url;

    try {
      const state = {
        surface: this.resolveRoute(url).surface,
        timestamp: Date.now()
      };

      if (replace) {
        window.history.replaceState(state, '', fullUrl);
      } else {
        window.history.pushState(state, '', fullUrl);
      }

      this.currentUrl = url;
    } catch (error) {
      console.error('Failed to update browser URL:', error);
    }
  }

  /**
   * Get all registered routes
   */
  public getRoutes(): RoutePattern[] {
    return Array.from(this.routes.values());
  }

  /**
   * Check if router is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[SurfaceRouter] ${message}`, data);
    }
  }
}

/**
 * Global router instance
 */
let globalRouter: SurfaceRouter | null = null;

/**
 * Create or get global router
 */
export function createSurfaceRouter(config?: RouterConfig): SurfaceRouter {
  if (!globalRouter) {
    globalRouter = new SurfaceRouter(config);
  }
  return globalRouter;
}

/**
 * Get global router
 */
export function getSurfaceRouter(): SurfaceRouter {
  if (!globalRouter) {
    globalRouter = new SurfaceRouter();
  }
  return globalRouter;
}

/**
 * React Hook: useSurfaceRouter
 */
export function useSurfaceRouter(): SurfaceRouter {
  return getSurfaceRouter();
}

export default SurfaceRouter;
